import { ParsedUrlQuery } from 'querystring';

import React, { memo, useEffect, useMemo } from 'react';

import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import GuestSupportBanner from '@components/ui/GuestsSuggestBanner';
import Typography from '@components/Typography';
import OpportunityCard from '@components/cards/opportunity/OpportunityCard';
import { Separator, Row, FlexibleDiv } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { MapLocationPinIcon } from '@assets/icons';
import {
  HotelAvailabilityStatusType,
  HotelDetailFeatureCategoryViewModel,
  HotelDetailReviewViewModel,
  HotelDetailViewModel,
  HotelDistanceViewModel,
  RatingProviderType,
  RoomLowestPriceViewModel,
  UserViewModel,
} from '@api/v3';
import { useAuthState } from '@contexts/auth/AuthContext';
import { useDevice } from '@contexts/device';
import { FavoriteItemType, HotelFeatureViewModel } from '@api/versions/v2';
import { haveLoyaltyPromotion } from '@components/views/travel-planning/utils';
import { formatHotelPromotions } from '@components/views/hotel/detail/utils/formats';
import { SocialShare } from '@components/ui/social-share';
import MyFavoriteComponent from '@components/views/my-favorites/MyFavoriteComponent';
import {
  useGetFavoriteListItems,
  useGetFavoriteLists,
} from '@components/views/my-favorites/hooks';
import { findDefaultList } from '@components/views/my-favorites/MyFavoriteComponent/utils';
import { useHotelDifference } from '@contexts/hotel-diff/HotelDifferenceContext';
import Difference from '@components/ui/cards/ListingItem/next/difference';
import { paramsToObject } from '@components/views/utils';
import useGetasPath from '@hooks/useGetasPath';

import {
  Title,
  TitleBorder,
  Container,
  Wrapper,
  SpacingRow,
  Location,
  ReadMore,
  IconContainer,
  StyledButtonMobile,
  CityWrapper,
  LocationWrapper,
} from './SummaryCardStyle';
import { getSkipassControl, getTransferServiceControl } from './utils';
import TimerTooltip from './timer-tooltip';

const DistanceMapComponent = dynamic(() => import('./distance-map-component'), {
  ssr: false,
});
const ScoringBanner = dynamic(() => import('@components/views/DetailScoring'));
const ExpediaDetailScoring = dynamic(
  () => import('@components/views/ExpediaDetailScoring')
);

const SummaryCardMobilePrice = dynamic(
  () => import('./SummaryCardMobilePrice')
);
const SummaryCardDesktopPrice = dynamic(
  () => import('./SummaryCardDesktopPrice')
);
const DescriptionsDesktop = dynamic(() => import('./DescriptionsDesktop'));
const DescriptionsMobile = dynamic(() => import('./DescriptionsMobile'));

const LoyaltyLoginBanner = dynamic(
  () => import('@components/ui/LoyaltyLoginBanner/LoyaltyLoginBanner'),
  { ssr: false }
);
export interface SummaryCardProps {
  onNavigate: () => void;
  onReadMore?: () => void;
  onFavorite?: () => void;
  onShare?: () => void;
  toggleMap?: () => void;
  onClickTripAdvisor?: () => void;
  name: string;
  boardType: string;
  description: string;
  city: string;
  country: string;
  prices?: RoomLowestPriceViewModel;
  features?: HotelFeatureViewModel[];
  roomInformation?: string;
  isExpedia?: boolean;
  isDomestic?: boolean;
  userInfo: UserViewModel;
  bankInstallMentForWorld?: string;
  status?: HotelAvailabilityStatusType;
  latitude?: number;
  longitude?: number;
  badge?: HotelDetailFeatureCategoryViewModel[];
  ratingDetail?: HotelDetailReviewViewModel;
  isBadge?: boolean;
  cityId?: number;
  promotions?: any;
  installments?: any;
  showKocAilem?: boolean;
  showInstallments?: () => void;
  hotelId?: number;
  imageUrl?: string;
  distanceList?: HotelDistanceViewModel[];
  region?: string;
  ispackage?: boolean;
  timeRemaining?: number;
  differenceItem?: HotelDetailViewModel;
  countryCode?: string;
  basketHotelCount?: number;
}
interface ReadMoreSectionProps {
  text: string;
  onClick?: () => void;
}
export const ReadMoreSection = ({ text, onClick }: ReadMoreSectionProps) => (
  <ReadMore onClick={onClick}>
    <Typography variant='caption2' color='BR300' weight='SemiBold'>
      {text}
    </Typography>
  </ReadMore>
);

const SummaryCard = ({
  onNavigate,
  toggleMap,
  name,
  boardType,
  city,
  country,
  prices,
  roomInformation,
  isExpedia = false,
  bankInstallMentForWorld,
  isDomestic,
  userInfo,
  latitude,
  longitude,
  badge,
  ratingDetail,
  isBadge,
  onClickTripAdvisor,
  promotions,
  installments,
  showKocAilem,
  showInstallments,
  hotelId,
  imageUrl,
  distanceList,
  region,
  ispackage,
  timeRemaining,
  differenceItem,
  countryCode,
  basketHotelCount,
}: SummaryCardProps) => {
  const { isDesktop } = useDevice();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const asPath = useGetasPath();

  const entryDateMissing = !query.in;
  const {
    diffData,
    setDiffData,
    addDiffData,
    removeDiffData,
    setVisibleInHeader,
  } = useHotelDifference();

  useEffect(() => {
    if (!differenceItem?.isExpedia) {
      setVisibleInHeader(true);
    } else {
      setVisibleInHeader(false);
    }
  }, [differenceItem]);

  useEffect(() => {
    return () => {
      setVisibleInHeader(false);
    };
  }, []);

  const showSeturEmployeePrice = isDomestic && userInfo?.isSeturEmployee;
  const link = encodeURIComponent(`${process.env.NEXT_PUBLIC_HOST}${asPath}`);

  const { loginInfo } = useAuthState();
  const isLoggedIn = !!loginInfo?.token;

  const { favoriteLists, refetchFavoriteLists } = useGetFavoriteLists({
    token: loginInfo?.token ?? '',
  });

  const defaultList = useMemo(() => {
    return findDefaultList(favoriteLists);
  }, [favoriteLists]);

  const {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
  } = useGetFavoriteListItems({
    wishListId: defaultList?.id ?? '',
    size: 1000,
  });

  return (
    <Container>
      {timeRemaining ? <TimerTooltip timeRemaining={timeRemaining} /> : null}
      <Wrapper>
        <SpacingRow>
          <Row>
            <TitleBorder />
            <Title>
              <Typography variant='Title3' color='D400' weight='Black'>
                {name}
              </Typography>
            </Title>
          </Row>

          <div>
            <IconContainer>
              {ispackage ? null : (
                <MyFavoriteComponent
                  itemId={hotelId}
                  itemName={name}
                  imageUrl={imageUrl ?? ''}
                  type={FavoriteItemType.HOTEL}
                  favoriteLists={favoriteLists}
                  refetchFavoriteLists={refetchFavoriteLists}
                  defaultList={defaultList}
                  defaultListItems={
                    isFavoriteListItemError ? [] : favoriteListItems?.data
                  }
                  refetchDefaultList={refetchFavoriteListItems}
                  fontColor='D400'
                />
              )}
              {ispackage ? null : isExpedia ? null : (
                <Difference
                  diffData={diffData}
                  setDiffData={setDiffData}
                  differenceItem={differenceItem}
                  addDiffData={addDiffData}
                  removeDiffData={removeDiffData}
                  isListing={false}
                />
              )}
              <SocialShare itemName={name} itemId={hotelId} link={link} />
            </IconContainer>
            {ratingDetail?.rating &&
            ratingDetail.provider === RatingProviderType.TRIP_ADVISOR ? (
              <ScoringBanner
                onClickTripAdvisor={onClickTripAdvisor}
                generalRatingImage={ratingDetail?.generalRatingImage}
                totalScoring={ratingDetail?.rating}
                subratings={ratingDetail?.subratings}
              />
            ) : null}
            {ratingDetail?.rating &&
            ratingDetail.provider === RatingProviderType.EXPEDIA ? (
              <ExpediaDetailScoring
                onClickTripAdvisor={onClickTripAdvisor}
                totalScoring={ratingDetail?.rating}
                subratings={ratingDetail?.subratings}
              />
            ) : null}
          </div>
        </SpacingRow>

        {isDesktop ? (
          <DescriptionsDesktop badge={badge} boardType={boardType} />
        ) : (
          <DescriptionsMobile badge={badge} boardType={boardType} />
        )}

        {isBadge ? (
          <FlexibleDiv bottom={isBadge && isDesktop ? 12 : 10}>
            <GuestSupportBanner type='hotel' />
          </FlexibleDiv>
        ) : null}

        <Row>
          <CityWrapper
            marginTop={
              boardType === '' &&
              !getSkipassControl(badge) &&
              !getTransferServiceControl(badge) &&
              !isBadge
            }
          >
            <LocationWrapper>
              {region ? (
                <Location>
                  <CustomIcon fontSize={20} icon={MapLocationPinIcon} />
                  {region} / {city}
                </Location>
              ) : (
                <Location>
                  <CustomIcon fontSize={20} icon={MapLocationPinIcon} />
                  {city} / {country}
                </Location>
              )}
              {isDesktop ? (
                <DistanceMapComponent
                  distanceList={distanceList}
                  latitude={latitude}
                  longitude={longitude}
                  toggleMap={toggleMap}
                />
              ) : null}
            </LocationWrapper>
          </CityWrapper>
        </Row>
        {!isDesktop ? (
          <DistanceMapComponent
            distanceList={distanceList}
            latitude={latitude}
            longitude={longitude}
            toggleMap={toggleMap}
          />
        ) : null}

        {!isDesktop ? (
          <SummaryCardMobilePrice
            prices={prices}
            isExpedia={isExpedia}
            onNavigate={onNavigate}
            bankInstallMentForWorld={bankInstallMentForWorld}
            entryDateMissing={entryDateMissing}
            showSeturEmployeePrice={showSeturEmployeePrice}
            roomInformation={roomInformation}
            isPackage={ispackage}
            countryCode={countryCode}
            basketHotelCount={basketHotelCount}
          />
        ) : null}
        {promotions?.length || installments?.length ? (
          <OpportunityCard
            showInstallments={showInstallments}
            showKocAilem={showKocAilem}
            isBankInstallments={Boolean(installments?.length > 0)}
            showMoreControl={Boolean(promotions?.length > 0)}
            items={formatHotelPromotions(promotions)}
          />
        ) : null}

        {promotions?.length &&
        haveLoyaltyPromotion(promotions) &&
        !isLoggedIn ? (
          <>
            <Separator size='10px' />
            <LoyaltyLoginBanner />
          </>
        ) : null}

        {!isDesktop ? (
          <>
            <Separator size='15px' />
            <StyledButtonMobile variant='Primary' onClick={onNavigate}>
              Odaları Gör
            </StyledButtonMobile>
          </>
        ) : null}
      </Wrapper>
      {isDesktop ? (
        <SummaryCardDesktopPrice
          prices={prices}
          isExpedia={isExpedia}
          onNavigate={onNavigate}
          bankInstallMentForWorld={bankInstallMentForWorld}
          entryDateMissing={entryDateMissing}
          showSeturEmployeePrice={showSeturEmployeePrice}
          roomInformation={roomInformation}
          isPackage={ispackage}
          countryCode={countryCode}
          basketHotelCount={basketHotelCount}
        />
      ) : null}
    </Container>
  );
};

export default memo(SummaryCard);
